import { useState, useEffect, type FormEvent } from 'react';
import { Card, CardHeader, CardContent } from '@/components/ui/card.js';
import { Badge } from '@/components/ui/badge.js';
import { Button } from '@/components/ui/button.js';
import { Input } from '@/components/ui/input.js';
import {
  Plus,
  AlertCircle,
  Search,
  Trash2,
  Edit3,
  X,
  Check,
  Sparkles,
  Copy,
  CheckCircle2,
  AlertTriangle,
  Zap,
  Users,
} from 'lucide-react';
import { api } from '@/lib/api.js';
import { useAuth } from '@/context/AuthContext.js';
import { Pagination } from '@/components/ui/Pagination.js';
import { SortButton } from '@/components/ui/SortButton.js';
import { DeleteConfirmModal } from '@/components/ui/DeleteConfirmModal.js';
import { Skeleton } from '@/components/ui/skeleton.js';

interface Standup {
  id: string;
  user_id: string;
  user_name?: string;
  karachi_date: string;
  yesterday: string;
  today: string;
  blockers?: string | null;
}

import type { StandupDigest } from '@/types/index.js';

export function StandupsPage() {
  const { user } = useAuth();
  const isOwner = user?.role === 'owner';

  const [standups, setStandups] = useState<Standup[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [memberFilter, setMemberFilter] = useState('');
  const [dateFilter, setDateFilter] = useState('');
  const [sortField, setSortField] = useState<string>('karachi_date');
  const [sortDir, setSortDir] = useState<'asc' | 'desc'>('desc');
  const [currentPage, setCurrentPage] = useState<number>(1);

  // Modals
  const [showModal, setShowModal] = useState(false);
  const [formData, setFormData] = useState({ yesterday: '', today: '', blockers: '' });

  const [editingStandup, setEditingStandup] = useState<Standup | null>(null);
  const [editFormData, setEditFormData] = useState({ yesterday: '', today: '', blockers: '' });

  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Daily Standup AI Executive Digest State
  const [digest, setDigest] = useState<StandupDigest | null>(null);
  const [copiedBrief, setCopiedBrief] = useState(false);

  // Delete Confirmation State
  const [deleteStandupTarget, setDeleteStandupTarget] = useState<{ id: string; name: string } | null>(null);

  // Close modals on Escape key
  useEffect(() => {
    if (!editingStandup && !showModal) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setEditingStandup(null);
        setShowModal(false);
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [editingStandup, showModal]);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3000);
  };

  const fetchDigest = async () => {
    try {
      const res = await api.get<{ data: StandupDigest }>('/api/v1/standups/digest');
      const d = res?.data || (res as any);
      if (d && (d.date || d.summary)) {
        setDigest(d);
      }
    } catch (e) {
      console.error('Failed to load standup digest:', e);
    }
  };

  const handleCopyBrief = async () => {
    const briefText = digest?.formatted_brief || digest?.executive_brief_markdown;
    if (!briefText) return;
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(briefText);
      } else {
        const textarea = document.createElement('textarea');
        textarea.value = briefText;
        textarea.style.position = 'fixed';
        textarea.style.opacity = '0';
        document.body.appendChild(textarea);
        textarea.select();
        document.execCommand('copy');
        document.body.removeChild(textarea);
      }
      setCopiedBrief(true);
      showToast('Executive brief copied to clipboard!');
      setTimeout(() => setCopiedBrief(false), 2500);
    } catch (err) {
      console.error('Failed to copy brief:', err);
      showToast('Failed to copy to clipboard');
    }
  };

  const fetchStandups = async () => {
    try {
      const res = await api.get<{ data: Standup[] }>('/api/v1/standups');
      const list = res?.data || (Array.isArray(res) ? res : []);
      setStandups(list);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchStandups();
    fetchDigest();
  }, []);

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    try {
      await api.post('/api/v1/standups', formData);
      setShowModal(false);
      setFormData({ yesterday: '', today: '', blockers: '' });
      showToast("Standup posted successfully!");
      fetchStandups();
      fetchDigest();
    } catch (e: any) {
      alert(e.message || 'Failed to submit standup');
    }
  };

  const handleOpenEdit = (s: Standup) => {
    setEditingStandup(s);
    setEditFormData({
      yesterday: s.yesterday || '',
      today: s.today || '',
      blockers: s.blockers || '',
    });
  };

  const handleSaveEdit = async (e: FormEvent) => {
    e.preventDefault();
    if (!editingStandup) return;
    try {
      await api.patch(`/api/v1/standups/${editingStandup.id}`, editFormData);
      setEditingStandup(null);
      showToast('Standup updated successfully!');
      fetchStandups();
      fetchDigest();
    } catch (e: any) {
      alert(e.message || 'Failed to update standup');
    }
  };

  const handleDeleteStandup = (id: string, name?: string) => {
    setDeleteStandupTarget({ id, name: name || 'this standup entry' });
  };

  const confirmDeleteStandup = async () => {
    if (!deleteStandupTarget) return;
    const { id } = deleteStandupTarget;
    try {
      await api.delete(`/api/v1/standups/${id}`);
      setDeleteStandupTarget(null);
      showToast('Standup deleted.');
      fetchStandups();
      fetchDigest();
    } catch (e: any) {
      alert(e.message || 'Failed to delete standup');
    }
  };

  const handleSort = (field: string) => {
    if (sortField === field) {
      setSortDir((prev) => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('asc');
    }
  };

  const uniqueMembers = Array.from(new Set(standups.map((s) => s.user_name).filter(Boolean)));

  const filtered = standups.filter((s) => {
    const q = search.toLowerCase();
    const matchesSearch = q
      ? (s.yesterday && s.yesterday.toLowerCase().includes(q)) ||
        (s.today && s.today.toLowerCase().includes(q)) ||
        (s.blockers && s.blockers.toLowerCase().includes(q)) ||
        (s.user_name && s.user_name.toLowerCase().includes(q)) ||
        (s.karachi_date && s.karachi_date.includes(q))
      : true;
    const matchesMember = memberFilter ? s.user_name === memberFilter || s.user_id === memberFilter : true;
    const matchesDate = dateFilter ? s.karachi_date === dateFilter : true;
    return matchesSearch && matchesMember && matchesDate;
  });

  const sorted = [...filtered].sort((a: any, b: any) => {
    let aVal = a[sortField];
    let bVal = b[sortField];
    if (aVal === null || aVal === undefined) aVal = '';
    if (bVal === null || bVal === undefined) bVal = '';
    if (typeof aVal === 'string') {
      return sortDir === 'asc' ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
    }
    return sortDir === 'asc' ? (aVal > bVal ? 1 : -1) : (aVal < bVal ? 1 : -1);
  });

  const pageSize = 25;
  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const validCurrentPage = Math.min(currentPage, totalPages);
  const paginated = sorted.slice((validCurrentPage - 1) * pageSize, validCurrentPage * pageSize);

  return (
    <div className="space-y-6">
      {/* Toast Feedback */}
      {toastMessage && (
        <div className="fixed top-16 right-6 z-50 bg-brand-navy text-white px-4 py-2.5 rounded-lg shadow-xl text-xs flex items-center space-x-2 animate-in fade-in slide-in-from-top-2">
          <Check className="h-4 w-4 text-brand-teal" />
          <span>{toastMessage}</span>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="page-title">Daily Standups</h1>
          <p className="helper-text mt-1">Asynchronous daily check-ins: Yesterday, Today, and Blockers (Asia/Karachi calendar).</p>
        </div>
        <Button
          onClick={() => setShowModal(true)}
          className="bg-brand-teal hover:bg-brand-teal-action text-white flex items-center space-x-1 font-semibold text-xs"
        >
          <Plus className="h-4 w-4" />
          <span>Submit Today's Standup</span>
        </Button>
      </div>

      {/* Daily Standup AI Executive Digest */}
      {digest && (() => {
        const submitted = digest.submitted_count ?? digest.participation?.submitted_count ?? 0;
        const totalMem = digest.total_members ?? digest.participation?.total_team_count ?? 0;
        const partRate = digest.participation_rate ?? digest.participation?.completion_rate_percent ?? 0;
        const isHealthy = digest.health === 'healthy' || digest.health_badge === 'healthy';
        const isCritical = digest.health === 'critical' || digest.health_badge === 'critical';

        const shippedList: string[] = (digest.shipped_yesterday && digest.shipped_yesterday.length > 0)
          ? digest.shipped_yesterday.map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''))
          : (digest.yesterday_highlights || []).map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''));

        const tacklingList: string[] = (digest.tackling_today && digest.tackling_today.length > 0)
          ? digest.tackling_today.map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''))
          : (digest.today_priorities || []).map((it: any) => typeof it === 'string' ? it : (it.user ? `${it.user}: ${it.content}` : it.content || ''));

        const rawBlockers = digest.active_blocker_strings && digest.active_blocker_strings.length > 0
          ? digest.active_blocker_strings
          : (digest.active_blockers || []);
        const blockerList: string[] = rawBlockers.map((b: any) => {
          if (typeof b === 'string') return b;
          const sev = (b.severity || 'ALERT').toUpperCase();
          const prj = b.project_name || 'Agency';
          const desc = b.blocker_description || b.task_title || b.title || 'Blocker';
          return `[${sev}] ${prj}: ${desc}`;
        });

        return (
          <Card className="border-teal-200/70 dark:border-teal-900/60 bg-gradient-to-br from-teal-50/40 via-white to-slate-50/60 dark:from-teal-950/20 dark:via-brand-surface dark:to-slate-900/30 shadow-sm overflow-hidden">
            <CardHeader className="py-3 px-4 border-b border-teal-100 dark:border-teal-900/40 bg-teal-50/30 dark:bg-teal-950/20 flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
              <div className="flex flex-wrap items-center gap-2">
                <div className="p-1.5 rounded-lg bg-teal-100 text-teal-700 dark:bg-teal-900/50 dark:text-teal-300">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div className="flex items-center space-x-2">
                  <h2 className="text-sm font-bold text-brand-navy dark:text-white tracking-tight">
                    Daily Standup AI Executive Digest
                  </h2>
                  <Badge variant="outline" className="font-mono text-[10px] text-teal-700 dark:text-teal-300 border-teal-300 dark:border-teal-700">
                    {digest.date} PKT
                  </Badge>
                </div>
              </div>

              <div className="flex flex-wrap items-center gap-2">
                {/* Participation Badge */}
                <Badge
                  variant="secondary"
                  className="text-[11px] font-mono bg-white dark:bg-slate-800 text-slate-700 dark:text-slate-200 border border-slate-200 dark:border-slate-700"
                  title={`${submitted} of ${totalMem} team members logged standup`}
                >
                  <Users className="w-3 h-3 mr-1 text-teal-600 inline" />
                  {submitted}/{totalMem} ({partRate}%)
                </Badge>

                {/* Health Badge */}
                {isHealthy ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-700 border border-emerald-200">
                    🟢 Team Healthy
                  </span>
                ) : isCritical ? (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-red-50 text-red-700 border border-red-200 animate-pulse">
                    🔴 Critical Blockers
                  </span>
                ) : (
                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-50 text-amber-700 border border-amber-200">
                    🟡 Attention Required
                  </span>
                )}

                {/* Copy Brief Button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={handleCopyBrief}
                  className="h-7 text-xs font-semibold bg-white dark:bg-slate-800 hover:bg-slate-50 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 border-slate-300 dark:border-slate-600 flex items-center space-x-1"
                  title="Copy formatted executive brief formatted for Slack/WhatsApp"
                >
                  {copiedBrief ? (
                    <>
                      <Check className="w-3.5 h-3.5 text-emerald-600" />
                      <span>Copied!</span>
                    </>
                  ) : (
                    <>
                      <Copy className="w-3.5 h-3.5 text-slate-500" />
                      <span>Copy Executive Brief</span>
                    </>
                  )}
                </Button>
              </div>
            </CardHeader>

            <CardContent className="p-4 space-y-3.5">
              {/* AI Synthesis Summary */}
              <p className="text-xs text-slate-700 dark:text-slate-300 leading-relaxed font-medium bg-white/80 dark:bg-slate-900/40 p-2.5 rounded-md border border-teal-100/60 dark:border-teal-950">
                {digest.summary}
              </p>

              {/* 3 Pillars Grid */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {/* Shipped Yesterday */}
                <div className="rounded-lg border border-emerald-100 dark:border-emerald-950 bg-emerald-50/30 dark:bg-emerald-950/20 p-3 space-y-1.5">
                  <div className="flex items-center space-x-1.5 text-emerald-800 dark:text-emerald-300 font-bold text-xs uppercase tracking-wide">
                    <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600 shrink-0" />
                    <span>Shipped Yesterday ({shippedList.length})</span>
                  </div>
                  {shippedList.length > 0 ? (
                    <ul className="text-xs text-slate-700 dark:text-slate-300 space-y-1 pl-1">
                      {shippedList.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-1.5">
                          <span className="text-emerald-500 shrink-0 font-bold">•</span>
                          <span className="leading-tight">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No items reported yesterday</p>
                  )}
                </div>

                {/* Tackling Today */}
                <div className="rounded-lg border border-blue-100 dark:border-blue-950 bg-blue-50/30 dark:bg-blue-950/20 p-3 space-y-1.5">
                  <div className="flex items-center space-x-1.5 text-blue-800 dark:text-blue-300 font-bold text-xs uppercase tracking-wide">
                    <Zap className="w-3.5 h-3.5 text-blue-600 shrink-0" />
                    <span>Tackling Today ({tacklingList.length})</span>
                  </div>
                  {tacklingList.length > 0 ? (
                    <ul className="text-xs text-slate-700 dark:text-slate-300 space-y-1 pl-1">
                      {tacklingList.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-1.5">
                          <span className="text-blue-500 shrink-0 font-bold">•</span>
                          <span className="leading-tight">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-slate-400 italic">No tasks logged for today</p>
                  )}
                </div>

                {/* Active Blockers */}
                <div className={`rounded-lg border p-3 space-y-1.5 ${
                  blockerList.length > 0
                    ? 'border-red-200 dark:border-red-950 bg-red-50/40 dark:bg-red-950/20'
                    : 'border-slate-200 dark:border-slate-800 bg-slate-50/40 dark:bg-slate-900/20'
                }`}>
                  <div className={`flex items-center space-x-1.5 font-bold text-xs uppercase tracking-wide ${
                    blockerList.length > 0
                      ? 'text-red-700 dark:text-red-400'
                      : 'text-slate-600 dark:text-slate-400'
                  }`}>
                    <AlertTriangle className="w-3.5 h-3.5 shrink-0" />
                    <span>Active Blockers ({blockerList.length})</span>
                  </div>
                  {blockerList.length > 0 ? (
                    <ul className="text-xs text-red-800 dark:text-red-300 space-y-1 pl-1 font-medium">
                      {blockerList.map((item, idx) => (
                        <li key={idx} className="flex items-start space-x-1.5">
                          <span className="text-red-500 shrink-0 font-bold">•</span>
                          <span className="leading-tight">{item}</span>
                        </li>
                      ))}
                    </ul>
                  ) : (
                    <p className="text-xs text-emerald-700 dark:text-emerald-400 font-medium">
                      No active blockers reported 🎉
                    </p>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        );
      })()}


      {/* Toolbar */}
      <div className="bg-white p-3 rounded-lg border border-slate-200 flex flex-col md:flex-row md:items-center justify-between gap-3">
        <div className="flex flex-wrap items-center gap-2 flex-1">
          <div className="relative flex-1 min-w-[200px] sm:max-w-xs">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-slate-400" />
            <Input
              type="text"
              placeholder="Search standups..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setCurrentPage(1);
              }}
              className="pl-9 h-9 text-xs"
            />
          </div>
          <select
            value={memberFilter}
            onChange={(e) => {
              setMemberFilter(e.target.value);
              setCurrentPage(1);
            }}
            className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs text-slate-700"
          >
            <option value="">All Members</option>
            {uniqueMembers.map((m) => (
              <option key={m} value={m}>
                {m}
              </option>
            ))}
          </select>
          <div className="flex items-center space-x-1">
            <input
              type="date"
              value={dateFilter}
              onChange={(e) => {
                setDateFilter(e.target.value);
                setCurrentPage(1);
              }}
              className="h-9 rounded-md border border-slate-200 bg-white px-2.5 text-xs text-slate-700"
            />
            {dateFilter && (
              <button
                type="button"
                onClick={() => {
                  setDateFilter('');
                  setCurrentPage(1);
                }}
                className="text-[11px] text-brand-teal hover:underline px-1 font-medium"
              >
                Clear
              </button>
            )}
          </div>
        </div>

        <div className="flex items-center space-x-3 text-xs text-slate-500">
          <span className="font-medium text-slate-400">Sort:</span>
          <SortButton label="Date" field="karachi_date" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <SortButton label="Member" field="user_name" currentField={sortField} currentDirection={sortDir} onSort={handleSort} />
          <span className="text-xs text-slate-400 font-medium pl-2 border-l border-slate-200">
            {sorted.length} {sorted.length === 1 ? 'entry' : 'entries'}
          </span>
        </div>
      </div>

      {/* Standup Feed */}
      <div className="space-y-4">
        {loading ? (
          <div className="space-y-4">
            {Array.from({ length: 4 }).map((_, i) => (
              <Card key={i} className="p-5 space-y-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <Skeleton className="h-5 w-32" />
                    <Skeleton className="h-4 w-20 rounded-full" />
                  </div>
                  <Skeleton className="h-4 w-24" />
                </div>
                <div className="space-y-2 pt-2">
                  <Skeleton className="h-3 w-5/6" />
                  <Skeleton className="h-3 w-2/3" />
                </div>
              </Card>
            ))}
          </div>
        ) : paginated.length === 0 ? (
          <Card>
            <CardContent className="p-8 text-center text-slate-500 text-sm">
              No standups found matching your search.
            </CardContent>
          </Card>
        ) : (
          paginated.map((s) => {
            const canEdit = isOwner || s.user_id === user?.id;
            return (
              <Card key={s.id} className="hover:border-slate-300 transition-all">
                <CardHeader className="py-3 px-4 border-b border-slate-100 flex flex-row items-center justify-between">
                  <div className="flex items-center space-x-2">
                    <div className="h-6 w-6 rounded-full bg-slate-100 flex items-center justify-center font-bold text-xs text-brand-navy">
                      {s.user_name?.substring(0, 2).toUpperCase() || 'EM'}
                    </div>
                    <span className="font-semibold text-sm text-brand-navy">{s.user_name}</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Badge variant="secondary" className="font-mono text-xs">
                      {s.karachi_date}
                    </Badge>
                    <div className="flex items-center space-x-1">
                      {canEdit && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleOpenEdit(s)}
                          className="h-7 w-7 text-slate-400 hover:text-brand-navy"
                          title="Edit Standup"
                        >
                          <Edit3 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                      {isOwner && (
                        <Button
                          size="icon"
                          variant="ghost"
                          onClick={() => handleDeleteStandup(s.id, `${s.user_name || 'Member'}'s standup (${s.karachi_date})`)}
                          className="h-7 w-7 text-slate-300 hover:text-red-600 hover:bg-red-50"
                          title="Delete Standup"
                        >
                          <Trash2 className="h-3.5 w-3.5" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="p-4 space-y-3 text-xs">
                  <div>
                    <span className="font-bold text-slate-700 block mb-0.5">Yesterday:</span>
                    <p className="text-slate-600 bg-slate-50 p-2 rounded">{s.yesterday}</p>
                  </div>
                  <div>
                    <span className="font-bold text-slate-700 block mb-0.5">Today:</span>
                    <p className="text-slate-600 bg-slate-50 p-2 rounded">{s.today}</p>
                  </div>
                  {s.blockers && (
                    <div>
                      <span className="font-bold text-red-700 block mb-0.5 flex items-center space-x-1">
                        <AlertCircle className="h-3.5 w-3.5" />
                        <span>Blockers:</span>
                      </span>
                      <p className="text-red-800 bg-red-50 p-2 rounded border border-red-100">{s.blockers}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })
        )}
        <Pagination
          totalItems={sorted.length}
          currentPage={validCurrentPage}
          pageSize={pageSize}
          onPageChange={setCurrentPage}
        />
      </div>

      {/* Submit Standup Modal */}
      {showModal && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setShowModal(false)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Submit Daily Standup</h2>
              <button onClick={() => setShowModal(false)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">What did you accomplish yesterday? *</label>
                <textarea
                  required
                  rows={3}
                  value={formData.yesterday}
                  onChange={(e) => setFormData({ ...formData, yesterday: e.target.value })}
                  placeholder="Completed Stripe checkout webhook, fixed CSS layout..."
                  className="w-full mt-1 rounded-md border border-slate-200 p-2.5 text-xs focus:ring-1 focus:ring-brand-teal focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">What are you working on today? *</label>
                <textarea
                  required
                  rows={3}
                  value={formData.today}
                  onChange={(e) => setFormData({ ...formData, today: e.target.value })}
                  placeholder="Building mobile task sheet, writing tests..."
                  className="w-full mt-1 rounded-md border border-slate-200 p-2.5 text-xs focus:ring-1 focus:ring-brand-teal focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Any blockers requiring manager support?</label>
                <textarea
                  rows={2}
                  value={formData.blockers}
                  onChange={(e) => setFormData({ ...formData, blockers: e.target.value })}
                  placeholder="Waiting for client API key access, design review..."
                  className="w-full mt-1 rounded-md border border-slate-200 p-2.5 text-xs focus:ring-1 focus:ring-brand-teal focus:outline-none"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setShowModal(false)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Post Standup
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Edit Standup Modal */}
      {editingStandup && (
        <div
          className="fixed inset-0 bg-slate-900/40 z-50 flex items-center justify-center p-4 animate-in fade-in"
          onClick={() => setEditingStandup(null)}
        >
          <div
            className="bg-white rounded-lg shadow-xl max-w-lg w-full p-6 space-y-4 animate-in fade-in zoom-in-95"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-brand-navy">Edit Daily Standup</h2>
              <button onClick={() => setEditingStandup(null)} className="text-slate-400 hover:text-slate-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <form onSubmit={handleSaveEdit} className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-slate-700">What did you accomplish yesterday? *</label>
                <textarea
                  required
                  rows={3}
                  value={editFormData.yesterday}
                  onChange={(e) => setEditFormData({ ...editFormData, yesterday: e.target.value })}
                  className="w-full mt-1 rounded-md border border-slate-200 p-2.5 text-xs focus:ring-1 focus:ring-brand-teal focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">What are you working on today? *</label>
                <textarea
                  required
                  rows={3}
                  value={editFormData.today}
                  onChange={(e) => setEditFormData({ ...editFormData, today: e.target.value })}
                  className="w-full mt-1 rounded-md border border-slate-200 p-2.5 text-xs focus:ring-1 focus:ring-brand-teal focus:outline-none"
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-slate-700">Any blockers requiring manager support?</label>
                <textarea
                  rows={2}
                  value={editFormData.blockers}
                  onChange={(e) => setEditFormData({ ...editFormData, blockers: e.target.value })}
                  className="w-full mt-1 rounded-md border border-slate-200 p-2.5 text-xs focus:ring-1 focus:ring-brand-teal focus:outline-none"
                />
              </div>
              <div className="flex justify-end space-x-2 pt-3 border-t border-slate-100">
                <Button type="button" variant="ghost" onClick={() => setEditingStandup(null)} className="text-xs">
                  Cancel
                </Button>
                <Button type="submit" className="bg-brand-teal text-white text-xs hover:bg-brand-teal-action">
                  Save Changes
                </Button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Shared Delete Confirmation Dialog */}
      <DeleteConfirmModal
        isOpen={!!deleteStandupTarget}
        itemType="Standup Entry"
        itemName={deleteStandupTarget?.name}
        onClose={() => setDeleteStandupTarget(null)}
        onConfirm={confirmDeleteStandup}
      />
    </div>
  );
}
